import type { ProductId } from '@/config/products';
import { PRODUCT_BY_ID } from '@/config/products';
import { getHomeRouteForRole } from '@/utils/homeRoute';
import type { WorkspaceContextPayload, WorkspaceLandingTarget } from '@/config/productRegistry';
import {
  canOpenProduct,
  isProductComingSoon,
  parseTenantProducts,
  shouldShowAidlcHub,
} from '@/utils/productAccess';
import { getOrgAdminLanding, needsOrgSetup } from '@/utils/orgConsole';

function productEntryRoute(
  productId: ProductId,
  role?: string | null,
  settings?: Record<string, unknown> | null,
): string {
  if (productId === 'pm') return getHomeRouteForRole(role, settings);
  const product = PRODUCT_BY_ID[productId];
  if (product.launchMode === 'internal') return product.routePrefix;
  return `/workspace?product=${productId}`;
}

/**
 * Resolve post-login path — backward compatible when workspace context absent.
 * Priority: entry product → stakeholder role home → org default → single product → hub → role home
 */
export function resolvePostLoginRoute(params: {
  settings?: Record<string, unknown> | null;
  role?: string | null;
  entryProductId?: ProductId | null;
  workspace?: WorkspaceContextPayload | null;
}): string {
  const { settings, role, entryProductId, workspace } = params;
  const access = parseTenantProducts(settings);
  const entry = entryProductId ?? null;

  if (role === 'STAKEHOLDER') {
    return getHomeRouteForRole(role);
  }

  // Org ADMIN: welcome setup first, then org console — not Plan / hub.
  if (role === 'ADMIN') {
    if (needsOrgSetup(role, settings)) {
      return getOrgAdminLanding(settings);
    }
    if (!entry || entry === 'pm') {
      return getOrgAdminLanding(settings);
    }
  }

  if (entry && entry !== 'pm') {
    if (canOpenProduct(entry, access, role)) {
      return productEntryRoute(entry, role, settings);
    }
    if (isProductComingSoon(entry, access)) {
      return productEntryRoute(entry, role, settings);
    }
  }

  if (workspace) {
    const landing = workspace.workspaceDefaults?.landingProductId;
    if (landing === 'hub' && workspace.routing.showHub) {
      return '/workspace';
    }
    if (landing && landing !== 'hub' && workspace.userLaunchableProductIds.includes(landing)) {
      return productEntryRoute(landing, role, settings);
    }
    if (workspace.routing.suggestedLanding === 'hub') {
      return '/workspace';
    }
    if (
      workspace.routing.suggestedLanding !== 'role_home' &&
      workspace.userLaunchableProductIds.includes(workspace.routing.suggestedLanding)
    ) {
      return productEntryRoute(workspace.routing.suggestedLanding, role, settings);
    }
  }

  if (shouldShowAidlcHub(access, role)) {
    return '/workspace';
  }

  return getHomeRouteForRole(role, settings);
}

export type { WorkspaceLandingTarget };
